// Libs
import clsx from "clsx";
import { trim } from "lodash";
import { flow } from "lodash/fp";
import React from "react";

// Utils
import { useTranslation } from "src/design-system/core/TranslationContext";
import { oldFormatNumber } from "src/design-system/core/utils/formators";
import { formatRangeFilter } from "./hooks";

// Components
import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import { Box, Button, InputAdornment, Popover, TextField, type Theme } from "@mui/material";

import { createStyles, makeStyles } from "@mui/styles";
import { useFormatOptions } from "src/hooks";
import { LanguageEnum } from "src/types/enum/LanguageEnum";

export interface RangeSelectFilterValue {
    min?: number;
    max?: number;
}

export interface RangeSelectFilerProps {
    concept: string;
    disabled?: boolean;
    emptyLabel: string;
    factor?: number;
    formatter: (value: number) => string;
    handleFilter: (value: RangeSelectFilterValue) => void;
    headerFilter: RangeSelectFilterValue;
    placeholder?: RangeSelectFilterValue;
    step?: number;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            margin: theme.spacing(1),
            display: "flex",
            gap: theme.spacing(1),
            alignItems: "center",
            fontSize: 12,
        },
        input: {
            fontSize: 12,
            marginTop: 2,
            width: 40,
            cursor: "pointer",
            '&[type="number"]': {
                "-webkit-appearance": "textfield",
                "-moz-appearance": "textfield",
                appearance: "textfield",
            },
            '&[type="number"]::-webkit-inner-spin-button': {
                "-webkit-appearance": "none",
            },
            '&[type="number"]::-webkit-outer-spin-button': {
                "-webkit-appearance": "none",
            },
        },
        field: {
            width: 150,
        },
        button: {
            "&:hover": {
                backgroundColor: "transparent",
            },
        },
    })
);

function applyFactor(number: number, factor: number) {
    return number !== undefined ? number * factor : undefined;
}

const applyIfDefined =
    <V, R>(callback: (value: V) => R) =>
    (value: V): R => {
        if (value === undefined) {
            return undefined;
        }

        return callback(value);
    };

export function RangeSelectFiler({
    concept,
    disabled = false,
    emptyLabel,
    factor = 1,
    formatter,
    handleFilter,
    headerFilter: { min: minProp, max: maxProp },
    placeholder: { min: placeholderMinProp, max: placeholderMaxProp } = {},
    step,
}: RangeSelectFilerProps): JSX.Element {
    const classes = useStyles();
    const { translateCommon, t: translateRaw } = useTranslation();
    const formatOptions = useFormatOptions();

    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement>(null);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (disabled) {
            return;
        }

        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => setAnchorEl(null);

    const open = Boolean(anchorEl);
    const id = open ? "simple-popover-range" : undefined;

    const adornmentPosition = formatOptions.language === LanguageEnum.GB ? "start" : "end";

    const textFieldProps = {
        InputProps: {
            [`${adornmentPosition}Adornment`]: (
                <InputAdornment position={adornmentPosition}>
                    {formatOptions.currencySymbol}
                </InputAdornment>
            ),
        },
        type: "number",
    };

    const handleRangeChangeFor =
        (key: keyof RangeSelectFilterValue) => (event: React.ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value;

            handleFilter({
                min: minProp,
                max: maxProp,
                [key]:
                    value !== undefined && trim(value) !== ""
                        ? applyFactor(Number.parseInt(value, 10), factor)
                        : undefined,
            });
        };

    const renderValue = formatRangeFilter(
        { min: minProp, max: maxProp },
        { concept, emptyLabel, formatter, translateRaw }
    );

    const placeholderMin = flow(
        (v) => applyFactor(v, 1 / factor),
        applyIfDefined(Math.floor)
    )(placeholderMinProp);
    const placeholderMax = flow(
        (v) => applyFactor(v, 1 / factor),
        applyIfDefined(Math.ceil)
    )(placeholderMaxProp);

    const min = applyFactor(minProp, 1 / factor);
    const max = applyFactor(maxProp, 1 / factor);

    return (
        <>
            <Button
                disabled={disabled}
                classes={{ root: classes.button }}
                disableFocusRipple
                aria-describedby={id}
                onClick={handleClick}
                disableRipple
            >
                <TextField
                    disabled={disabled}
                    slotProps={{
                        htmlInput: {
                            className: clsx(classes.input, classes.field),
                        },
                        input: {
                            disableUnderline: true,
                            readOnly: true,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <ArrowDropDown style={{ opacity: 0.7 }} />
                                </InputAdornment>
                            ),
                        },
                    }}
                    value={renderValue}
                />
            </Button>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                }}
            >
                <Box className={classes.container}>
                    <span>
                        {concept} {translateCommon("word-link.from-budget")} :
                    </span>
                    <TextField
                        {...textFieldProps}
                        inputProps={{
                            className: classes.input,
                            min: placeholderMin,
                            max: Math.min(
                                placeholderMax ?? Number.MAX_VALUE,
                                max ?? Number.MAX_VALUE
                            ),
                            step,
                        }}
                        error={
                            min !== undefined &&
                            (min < placeholderMin ||
                                min > placeholderMax ||
                                (max !== undefined && min > max))
                        }
                        value={min}
                        placeholder={oldFormatNumber(placeholderMin)}
                        onChange={handleRangeChangeFor("min")}
                    />
                    <span>{translateCommon("word-link.to-budget")}</span>
                    <TextField
                        {...textFieldProps}
                        inputProps={{
                            className: classes.input,
                            min: Math.max(
                                placeholderMin ?? Number.MIN_VALUE,
                                min ?? Number.MIN_VALUE
                            ),
                            max: placeholderMax,
                            step,
                        }}
                        error={
                            max !== undefined &&
                            (max > placeholderMax ||
                                max < placeholderMin ||
                                (min !== undefined && max < min))
                        }
                        value={max}
                        placeholder={oldFormatNumber(placeholderMax)}
                        onChange={handleRangeChangeFor("max")}
                    />
                </Box>
            </Popover>
        </>
    );
}
